import { AxiosError } from 'axios'
import {
  useQuery,
  type QueryObserverOptions,
  type QueryFunctionContext
} from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { prometheusQueryKeys } from 'queries/prometheus/query-keys'
import {
  type PrometheusMonitorTestEndpointResponse,
  type PrometheusMonitorTestEndpointResponseData
} from 'queries/prometheus/types'
import { isSearchPrometheusMonitorsSuccessResponse } from 'queries/prometheus/utils'
import { getEndpointWithParams } from 'queries/queries-utils'

async function searchPrometheusMonitors({
  queryKey
}: QueryFunctionContext<
  ReturnType<typeof prometheusQueryKeys.searchMonitors>
>) {
  const endpointWithParams = getEndpointWithParams(...queryKey)
  const response = await agent.get<PrometheusMonitorTestEndpointResponse>(
    endpointWithParams
  )

  if (!isSearchPrometheusMonitorsSuccessResponse(response.data)) {
    const errorMessageSplit = response.data.error?.split(': ') ?? []
    const responseCode = errorMessageSplit[1]
      ? parseInt(errorMessageSplit[1])
      : '400'

    throw new AxiosError(
      response.data.error,
      responseCode.toString(),
      response.config,
      response.request,
      response
    )
  }

  return response.data.data
}

export function useSearchPrometheusMonitors(
  integrationName: string,
  searchString: string,
  options?: Omit<
    QueryObserverOptions<PrometheusMonitorTestEndpointResponseData, AxiosError>,
    'queryKey' | 'queryFn'
  >
) {
  const { currentTeamId } = useAuth()

  return useQuery({
    ...options,
    queryFn: searchPrometheusMonitors,
    queryKey: prometheusQueryKeys.searchMonitors({
      prometheusIntegrationName: integrationName,
      searchString,
      teamId: currentTeamId
    })
  })
}
